import {
  getProductDetail,
  productDetailRoute,
  productDetailHomeHref,
  productDetailServiceHref,
  productDetailSignupHref,
  productDetailContactHref,
} from "../../../config/productDetails.js";
import { publicAsset, pageRoute } from "../../../config/paths.js";
import { buildAnimatedTitle } from "../../HeroTitleAnimation.js";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

export function createProductDetailHero(detail) {
  const section = document.createElement("section");
  section.id = "overview";
  section.className = "section product-detail-hero";

  const imageSrc = publicAsset(detail.image);
  const titleSegments = detail.titleSegments ?? [
    { text: `${detail.title}`, highlight: null },
  ];

  section.innerHTML = `
    <div class="product-detail-hero__atmosphere" aria-hidden="true">
      <span class="product-detail-hero__orb product-detail-hero__orb--a"></span>
      <span class="product-detail-hero__orb product-detail-hero__orb--b"></span>
      <span class="product-detail-hero__grid"></span>
    </div>

    <div class="section__inner product-detail-hero__inner">
      <nav class="product-detail-hero__crumb reveal" aria-label="Breadcrumb">
        <a href="${productDetailHomeHref()}">Our Portfolios</a>
        <span aria-hidden="true">/</span>
        <a href="${productDetailServiceHref(detail)}">${escapeHtml(detail.serviceLabel)}</a>
        <span aria-hidden="true">/</span>
        <span>${escapeHtml(detail.category)}</span>
      </nav>

      <div class="product-detail-hero__layout">
        <div class="product-detail-hero__copy">
          <p class="product-detail-hero__eyebrow reveal">${escapeHtml(detail.eyebrow)}</p>
          <h1 class="product-detail-hero__title reveal">${buildAnimatedTitle(titleSegments)}</h1>
          <p class="product-detail-hero__lead reveal">${escapeHtml(detail.headline)}</p>
          <p class="product-detail-hero__body reveal">${escapeHtml(detail.lead)}</p>
          <div class="product-detail-hero__actions reveal">
            <a class="btn btn--primary" href="${productDetailSignupHref()}">Register to start</a>
            <a class="btn btn--ghost" href="${productDetailContactHref()}">Talk to Taldel</a>
          </div>
          <ul class="product-detail-hero__meta reveal">
            <li><span>Lane</span><strong>${escapeHtml(detail.serviceLabel)}</strong></li>
            <li><span>Category</span><strong>${escapeHtml(detail.category)}</strong></li>
            <li><span>Status</span><strong>${escapeHtml(detail.status || "Dummy showcase")}</strong></li>
          </ul>
        </div>

        <div class="product-detail-hero__stage reveal" style="--delay: 0.12s">
          <div class="product-detail-hero__frame">
            <img src="${imageSrc}" alt="${escapeHtml(detail.imageAlt || detail.title)}" decoding="async" />
            <div class="product-detail-hero__frame-glow" aria-hidden="true"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  return section;
}

export function createProductDetailBody(detail) {
  const section = document.createElement("section");
  section.id = "details";
  section.className = "section product-detail-body";

  const related = (detail.related || [])
    .map((slug) => getProductDetail(slug))
    .filter(Boolean);

  section.innerHTML = `
    <div class="section__inner product-detail-body__inner">
      <div class="product-detail-metrics reveal">
        ${detail.metrics
          .map(
            (metric) => `
          <article class="product-detail-metric">
            <p class="product-detail-metric__value">${escapeHtml(metric.value)}</p>
            <p class="product-detail-metric__label">${escapeHtml(metric.label)}</p>
          </article>
        `
          )
          .join("")}
      </div>

      <div class="product-detail-split">
        <article class="product-detail-story reveal">
          <p class="product-detail-kicker">Overview</p>
          <h2>What this product actually delivers</h2>
          <p>${escapeHtml(detail.overview)}</p>
          <ul class="product-detail-outcomes">
            ${detail.outcomes
              .map((item) => `<li>${escapeHtml(item)}</li>`)
              .join("")}
          </ul>
        </article>

        <aside class="product-detail-stack reveal" style="--delay: 0.08s">
          <p class="product-detail-kicker">Stack signals</p>
          <h3>Built with intent</h3>
          <ul>
            ${detail.stack.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}
          </ul>
          <a class="btn btn--ghost" href="${productDetailServiceHref(detail)}">
            Explore ${escapeHtml(detail.serviceLabel)}
          </a>
        </aside>
      </div>

      <div class="product-detail-capabilities">
        <div class="product-detail-capabilities__head reveal">
          <p class="product-detail-kicker">Capabilities</p>
          <h2>The system under the surface</h2>
        </div>
        <ol class="product-detail-capabilities__list">
          ${detail.capabilities
            .map(
              (cap, index) => `
            <li class="product-detail-capability reveal" style="--delay: ${(index * 0.05).toFixed(2)}s">
              <span class="product-detail-capability__index">${String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>${escapeHtml(cap.title)}</h3>
                <p>${escapeHtml(cap.body)}</p>
              </div>
            </li>
          `
            )
            .join("")}
        </ol>
      </div>

      <div class="product-detail-timeline reveal">
        <div class="product-detail-timeline__head">
          <p class="product-detail-kicker">Delivery path</p>
          <h2>How we bring it to life</h2>
        </div>
        <ol class="product-detail-timeline__track">
          ${detail.timeline
            .map(
              (item) => `
            <li>
              <span class="product-detail-timeline__step">${escapeHtml(item.step)}</span>
              <h3>${escapeHtml(item.title)}</h3>
              <p>${escapeHtml(item.body)}</p>
            </li>
          `
            )
            .join("")}
        </ol>
      </div>

      ${
        related.length
          ? `
        <div class="product-detail-related">
          <div class="product-detail-related__head reveal">
            <p class="product-detail-kicker">Continue</p>
            <h2>Related product surfaces</h2>
          </div>
          <div class="product-detail-related__grid">
            ${related
              .map(
                (item) => `
              <a class="product-detail-related__card reveal" href="${productDetailRoute(item.slug)}">
                <img src="${publicAsset(item.image)}" alt="" loading="lazy" decoding="async" />
                <div>
                  <span>${escapeHtml(item.category)}</span>
                  <h3>${escapeHtml(item.title)}</h3>
                  <p>${escapeHtml(item.lead)}</p>
                </div>
              </a>
            `
              )
              .join("")}
          </div>
        </div>
      `
          : ""
      }

      <div class="product-detail-cta reveal" id="next">
        <div>
          <p class="product-detail-kicker">Next step</p>
          <h2>Ready to make this real?</h2>
          <p>Want a similar outcome? Register to start a conversation, or talk with us about a live build.</p>
        </div>
        <div class="product-detail-cta__actions">
          <a class="btn btn--primary" href="${productDetailSignupHref()}">Register</a>
          <a class="btn btn--ghost" href="${pageRoute("our-portfolios")}">Back to Our Portfolios</a>
        </div>
      </div>
    </div>
  `;

  return section;
}
